// Display · WeightValue
//
// Locale-formatted weight renderer. Closes the formatter-drift gap across
// the codebase — 6+ sites used `${n} t` raw templates producing inconsistent
// decimal formatting (no thousands sep, trailing zeros, Polish locale violations).
//
// Delegates to `TextValue` for all styling — WeightValue owns the number
// formatting + the optional `planned` delta hint. Renders `EmptyValue` for
// null/undefined inputs (consistent with every other display primitive).
//
// `planned` slot subsumes the former `EditableWeight` wrapper. Schema authors
// declaring inline-editable weight cells now compose:
//   { component: WeightValue, data: r => ({ value: r.unloaded, planned: r.planned }), edit: { ... } }
// `renderCell` adds the `<Editable>` wrap automatically when `ctx.canEdit` —
// no need for an "Editable-weight" intermediate primitive.

import type { ReactNode } from "react";
import { TextValue, type TextWeight } from "@ds/display/TextValue";
import { EmptyValue } from "@ds/display/EmptyValue";
import { Stack } from "@ds/layout/Stack";
import { withExportSpec } from "@ds/display/exportSpec";

// ─── Formatter ────────────────────────────────────────────────────────────
//
// pl-PL locale: 1 234,567 (space thousands, comma decimal)
// Always 3 fractional digits — weights read as a fixed-precision quantity
// (25,950 t, not 25,95 t), matching useFormat().weight() and formatMass3.

const FORMATTER = new Intl.NumberFormat("pl-PL", {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface WeightValueInput {
  /**
   * Weight value in the unit specified by `unit`. Pass `null` or `undefined`
   * to render `EmptyValue` (em-dash) — same convention as AmountDisplay.
   */
  value: number | null | undefined;
  /**
   * Unit suffix appended after a non-breaking space. Default `"t"` (metric
   * tons). Pass `"kg"`, `"szt"`, `"m³"`, etc. for other units.
   */
  unit?: string;
  /** Font size scale. Delegates to `TextValue`. Default = TextValue's default ("sm"). */
  size?: "xs" | "sm" | "md";
  /** Font weight. Delegates to `TextValue`. Default = regular. */
  weight?: TextWeight;
  /** Text alignment. Delegates to `TextValue`. Default = left. */
  align?: "left" | "right";
  /**
   * Planned/expected weight. When set AND non-zero diff from `value`, renders
   * a compact delta line below the value (`Δ +1.234 t` in green when over,
   * `Δ −0.500 t` in red when under). When null/undefined/zero-diff: no extra DOM.
   *
   * Subsumes the legacy `EditableWeight.planned` prop — schemas declare a
   * `planned` accessor on the same field, no wrapper primitive needed.
   */
  planned?: number | null;
  /** data-testid on the root element. */
  testId?: string;
}

// Atom — no Output, no Actions interfaces.
export type WeightValueProps = WeightValueInput;

// ─── Component ────────────────────────────────────────────────────────────

export function WeightValue({
  value,
  unit = "t",
  size,
  weight,
  align,
  planned,
  testId,
}: WeightValueProps): ReactNode {
  if (value == null) return <EmptyValue testId={testId} />;

  // Non-breaking space between number and unit keeps them together on line-wrap.
  const formatted = `${FORMATTER.format(value)}\u00A0${unit}`;

  const valueNode = (
    <TextValue size={size} weight={weight} align={align} testId={testId}>
      {formatted}
    </TextValue>
  );

  const delta = renderDelta(value, planned, unit);
  if (!delta) return valueNode;

  return (
    <Stack gap="xs">
      {valueNode}
      {delta}
    </Stack>
  );
}

// ─── Planned-delta hint ──────────────────────────────────────────────────
//
// Renders `Δ +N t` / `Δ −N t` when actual diverges from planned. Tone:
// "revenue" (green) when actual > planned (over-delivered), "cost" (red)
// when under. No node when planned is null/undefined or the diff rounds to 0.

function renderDelta(
  value: number,
  planned: number | null | undefined,
  unit: string,
): ReactNode {
  if (planned == null) return null;
  const diff = Math.round((value - planned) * 1000) / 1000;
  if (diff === 0) return null;
  const sign = diff > 0 ? "+" : "−";
  // TextValue tone enum: `revenue` (positive/green), `cost` (negative/red).
  const tone = diff > 0 ? "revenue" : "cost";
  return (
    <TextValue size="xs" tone={tone} testId="weight-value-delta">
      {`Δ ${sign}${FORMATTER.format(Math.abs(diff))} ${unit}`}
    </TextValue>
  );
}

// ─── Excel export ─────────────────────────────────────────────────────────
//
// Exports as a native number with the unit carried in the format, not glued
// into the string — so "24,500 t" becomes 24.5 that Excel still displays with
// its unit. The planned-delta hint is presentation-only and is not exported.

withExportSpec<typeof WeightValue, WeightValueProps>(WeightValue, {
  type: "weight",
  value: (p) => p.value,
  numFmt: (p) => `#,##0.000" ${p.unit ?? "t"}"`,
});
